import React, { useEffect, useMemo, useRef, useState } from "react";
import TransactionStyles from "../Transactions/styles";
import PageTitle from "../../../components/common/PageTitle/PageTitle";
import Table2 from "../../../components/common/Table/Table2";
import Search from "../../../components/common/Search";
import DateRangePicker from "../../../components/common/DateRange";
import ModernSelect from "../../../components/common/ModernSelect";
import restService from "../../../services/restService";
import { useSelector } from "react-redux";
import moment from "moment";
import { addDays } from "date-fns";






const currency = (symbol, value) => `${symbol} ${Number(value || 0).toLocaleString()}`;

const TransactionHistory = () => {
  const user = useSelector((s) => s?.auth?.user);
  const currencySymbol = useSelector((s) => s?.auth?.user?.agency?.country?.currency_symbol) || "";

  // Resolve role and known ids from user shape
  const roleName = user?.agent_role?.name || user?.staff_role?.name || "";
  const userAgentId = user?.id;
  const userAgencyId = user?.agency?.id;

  const didInitialLoad = useRef(false);

  const [showDateRange, setShowDateRange] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      // Default to the last 7 days
      startDate: addDays(new Date(), -7),
      endDate: new Date(),
      key: "selection",
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [agentFilter, setAgentFilter] = useState("");
  const [agencyFilter, setAgencyFilter] = useState("");
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);

  const [rows, setRows] = useState([]); // display rows from backend (or fallback)
  const [loading, setLoading] = useState(false);

  // Load dropdown data (role-aware)
  useEffect(() => {
    const load = async () => {
      try {
        if (roleName === "Administrator") {
          const agenciesRes = await restService.AgencyApi.getAgencies(1, "allItems");
          const agenciesData = agenciesRes?.data?.data || [];
          setAgencies(agenciesData);
          if (agencyFilter) {
            const agentsRes = await restService.AgentApi.getAgentsByAgency(agencyFilter, 1, "allItems");
            const agentsData = agentsRes?.data?.data || [];
            setAgents(agentsData);
          } else {
            setAgents([]);
          }
        } else if (roleName === "Super Agent") {
          if (userAgencyId) {
            setAgencyFilter((prev) => prev || String(userAgencyId));
            const agentsRes = await restService.AgentApi.getAgentsByAgency(userAgencyId, 1, "allItems");
            const agentsData = agentsRes?.data?.data || [];
            setAgents(agentsData);
          }
        } else {
          setAgencies([]);
          setAgents([]);
        }
      } catch (e) {
        // silent
      }
    };
    load();
  }, [agencyFilter, roleName]);

  // Build a display row from backend item
  const mapBackendItemToRow = (item) => {
    const type = item?.type || "EOD";
    const date = item?.date;
    let dateLabel = "";

    if (type === "EOD") {
      dateLabel = moment(date).format("MMM D, YYYY");
    } else if (type === "EOW") {
      const start = moment(date).startOf("isoWeek"); // Monday
      const end = moment(date).endOf("isoWeek"); // Sunday
      dateLabel = `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    } else if (type === "EOM") {
      dateLabel = moment(date).format("MMM YYYY");
    } else {
      dateLabel = moment(date).format("MMM D, YYYY");
    }

    return {
      type,
      dateLabel,
      dateRaw: date, // keep raw date for robust sorting
      sendAmount: item?.transaction_sum ?? 0,
      charges: item?.charge_sum ?? 0,
      agentCommission: item?.agent_commission_sum ?? 0,
    };
  };

  // Fetch summary from backend based on filters and date range
  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const start = moment(dateRange[0]?.startDate || addDays(new Date(), -7)).format("YYYY-MM-DD");
        const end = moment(dateRange[0]?.endDate || new Date()).format("YYYY-MM-DD");
        const isSingleDay = start === end;

        // Role-based first load API rules
        let params = { page: 1 };
        if (!didInitialLoad.current) {
          // Include a default last-7-days range on first load
          if (isSingleDay) {
            params.date = start;
          } else {
            params.startDate = start;
            params.endDate = end;
          }
          if (roleName === "Agent" && userAgentId) {
            params.agentId = String(userAgentId);
          } else if (roleName === "Super Agent" && userAgencyId) {
            params.agencyId = String(userAgencyId);
          }
        } else {
          // After first load, apply selected filters
          if (agentFilter) params.agentId = agentFilter;
          if (agencyFilter) {
            params.agencyId = agencyFilter;
          } else if (roleName === "Super Agent" && userAgencyId) {
            // Ensure Super Agent defaults to their own agency when no agent is selected
            params.agencyId = String(userAgencyId);
          }
          if (dateRange?.[0]?.startDate && dateRange?.[0]?.endDate) {
            if (isSingleDay) {
              delete params.startDate;
              delete params.endDate;
              params.date = start;
            } else {
              params.startDate = start;
              params.endDate = end;
            }
          }
        }

        const res = await restService.TransactionsApi.getTransactionSummary(params);

        const items = res?.data?.data?.data || [];
        if (Array.isArray(items) && items.length) {
          const mapped = items.map(mapBackendItemToRow);

          // Sort by date ascending; for same date, EOD < EOW < EOM
          const typeWeight = { EOD: 0, EOW: 1, EOM: 2 };
          mapped.sort((a, b) => {
            const da = new Date(a.dateRaw);
            const db = new Date(b.dateRaw);
            if (da.getTime() !== db.getTime()) return da - db;
            return (typeWeight[a.type] ?? 99) - (typeWeight[b.type] ?? 99);
          });

          setRows(mapped);
        } else {
          // No mock fallback: show empty if backend has no items
          setRows([]);
        }
      } catch (e) {
        // No mock fallback on error: clear rows
        setRows([]);
      } finally {
        setLoading(false);
        didInitialLoad.current = true;
      }
    };

    fetchSummary();
  }, [dateRange, agentFilter, agencyFilter, roleName, userAgentId, userAgencyId]);

  // Client-side search filter
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter(
      (r) => r.type.toLowerCase().includes(q) || r.dateLabel.toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  return (
    <div className="mt-13-important">
      <TransactionStyles>
        <PageTitle title="Transaction History" />
        <div className="content-layout">
          <div className="header-layout gap-2">
            <div className="flex md:items-center w-full gap-2 md:flex-row flex-col ">
              <Search placeholder="Search by Type or Date" onChange={(e) => setSearchTerm(e.target.value)} />
            </div>

            <div className="flex flex-col-reverse w-full justify-end sm:flex-row md:items-center">
              <div className="gap-4 flex justify-end md:justify-end sm:flex-row items-center md:mr-3">
                <div className="flex items-center gap-2 w-full md:w-auto">
                  {roleName === "Administrator" && (
                    <>
                      <div className="w-56">
                        <ModernSelect
                          label="Agency"
                          value={agencyFilter}
                          onChange={(e) => { setAgencyFilter(e.target.value); setAgentFilter(""); }}
                          options={agencies.map(a => ({ value: String(a.id), label: a.name }))}
                          placeholder="Select agency"
                        />
                      </div>
                      <div className="w-56">
                        <ModernSelect
                          label="Agent"
                          value={agentFilter}
                          onChange={(e) => setAgentFilter(e.target.value)}
                          options={agents.map(a => ({ value: String(a.id), label: `${a.firstname} ${a.lastname}` }))}
                          placeholder="Select agent"
                          disabled={!agencyFilter}
                        />
                      </div>
                    </>
                  )}
                  {roleName === "Super Agent" && (
                    <div className="w-56">
                      <ModernSelect
                        label="Agent"
                        value={agentFilter}
                        onChange={(e) => setAgentFilter(e.target.value)}
                        options={agents.map(a => ({ value: String(a.id), label: `${a.firstname} ${a.lastname}` }))}
                        placeholder="Select agent"
                      />
                    </div>
                  )}
                </div>
                <DateRangePicker
                  range={dateRange}
                  show={showDateRange}
                  setShow={setShowDateRange}
                  setRange={setDateRange}
                />
              </div>
            </div>
          </div>

          <div className="header-layout gap-2">
            <div className="flex w-full flex-col">
              <Table2
                tHead={
                  <>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Transaction</th>
                    <th>Charges</th>
                    <th>Agent Commission</th>
                  </>
                }
              >
                {filteredRows.map((item, idx) => {
                  const getRowStyling = (type) => {
                    switch (type) {
                      case "EOD":
                        return {
                          rowClass: "text-[#222] bg-white",
                          typeClass: "text-[#111]",
                          dateClass: "text-[#666]",
                          valueClass: "text-[#111]"
                        };
                      case "EOW":
                        return {
                          rowClass: "bg-blue-100 hover:bg-blue-150 transition-colors border-l-4 border-blue-500",
                          typeClass: "text-blue-900 font-semibold",
                          dateClass: "text-blue-800",
                          valueClass: "text-blue-900 font-medium"
                        };
                      case "EOM":
                        return {
                          rowClass: "bg-emerald-100 hover:bg-emerald-150 transition-colors border-l-4 border-emerald-600",
                          typeClass: "text-emerald-900 font-bold",
                          dateClass: "text-emerald-800",
                          valueClass: "text-emerald-900 font-semibold"
                        };
                      default:
                        return {
                          rowClass: "",
                          typeClass: "",
                          dateClass: "",
                          valueClass: ""
                        };
                    }
                  };

                  const styling = getRowStyling(item.type);

                  return (
                    <tr key={idx} className={styling.rowClass}>
                      <td>
                        <p className={styling.typeClass}>{item.type}</p>
                      </td>
                      <td>
                        <p className={styling.dateClass}>{item.dateLabel}</p>
                      </td>
                      <td>
                        <p className={styling.valueClass}>{currency(currencySymbol, item.sendAmount)}</p>
                      </td>
                      <td>
                        <p className={styling.valueClass}>{currency(currencySymbol, item.charges)}</p>
                      </td>
                      <td>
                        <p className={styling.valueClass}>{currency(currencySymbol, item.agentCommission)}</p>
                      </td>
                    </tr>
                  );
                })}
              </Table2>
              {loading && (
                <div className="py-3 text-sm text-gray-500">Loading summary...</div>
              )}
            </div>
          </div>
        </div>
      </TransactionStyles>
    </div>
  );
};

export default TransactionHistory;